// src/components/booking/BookingSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  X, 
  Plus,
  User,
  Save, 
  Upload, 
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle,
  Monitor,
  Smartphone,
  Tablet,
  Download,
  Type,
  Layout,
  Palette,
  Heart,
  Mail,
  Lock,
  Globe,
  Settings,
  Code,
  Bed,
  Clock,
  Car,
  Building2,
  Users,
  CreditCard,
  MapPin,
  Phone,
  Star,
  Shield,
  TrendingUp,
  Zap,
  Image as ImageIcon,
  FileText,
  Camera,
  Link,
  DollarSign,
  BarChart3,
  MessageCircle,
  ExternalLink,
  Wifi,
  Coffee,
  ParkingCircle,
  UtensilsCrossed
} from 'lucide-react';
import { FileUpload } from '../common/FileUpload';
import { firestoreService } from '../../services/firestoreService';
import { bookingService } from '../../services/bookingService';

// Booking Type Configurations
const BOOKING_TYPES = {
  accommodation: {
    label: 'Accommodation',
    icon: Bed,
    color: '#3b82f6',
    description: 'Hotels, B&Bs, vacation rentals, and lodging',
    defaultFeatures: ['calendar', 'reviews', 'gallery', 'pricing', 'availability'],
    fields: ['checkIn', 'checkOut', 'guests', 'rooms', 'amenities']
  },
  appointment: {
    label: 'Appointments', 
    icon: Clock,
    color: '#10b981',
    description: 'Medical, beauty, professional services, and consultations',
    defaultFeatures: ['calendar', 'reviews', 'availability', 'reminders'],
    fields: ['date', 'time', 'duration', 'service', 'practitioner']
  },
  rental: {
    label: 'Rentals',
    icon: Car, 
    color: '#f59e0b',
    description: 'Vehicle rentals, equipment hire, and tool lending',
    defaultFeatures: ['calendar', 'inventory', 'pricing', 'insurance'],
    fields: ['pickupDate', 'returnDate', 'location', 'insurance']
  },
  venue: {
    label: 'Venues',
    icon: Building2,
    color: '#8b5cf6', 
    description: 'Event spaces, halls, meeting rooms, and studios',
    defaultFeatures: ['calendar', 'capacity', 'catering', 'setup'],
    fields: ['date', 'time', 'duration', 'capacity', 'catering']
  }
};

const BUSINESS_MODELS = {
  single: {
    label: 'Single Vendor',
    description: 'You manage all bookings directly',
    icon: Users,
    features: ['direct-booking', 'simple-management', 'full-control']
  },
  multi: {
    label: 'Multi-Vendor Marketplace', 
    description: 'Multiple vendors can list their services',
    icon: Building2,
    features: ['vendor-registration', 'commission-system', 'vendor-management']
  }
};

const BOOKING_FEATURES = {
  calendar: { label: 'Calendar Management', icon: Calendar, description: 'Advanced calendar with availability management' },
  reviews: { label: 'Reviews & Ratings', icon: Star, description: 'Customer reviews and rating system' },
  gallery: { label: 'Photo Gallery', icon: Camera, description: 'Rich media galleries for listings' },
  pricing: { label: 'Dynamic Pricing', icon: DollarSign, description: 'Flexible pricing with seasonal rates' },
  availability: { label: 'Real-time Availability', icon: Clock, description: 'Live availability checking' },
  reminders: { label: 'Email Reminders', icon: Mail, description: 'Automated reminder system' },
  inventory: { label: 'Inventory Tracking', icon: BarChart3, description: 'Track item availability and stock' },
  insurance: { label: 'Insurance Options', icon: Shield, description: 'Insurance and protection plans' },
  capacity: { label: 'Capacity Management', icon: Users, description: 'Manage venue capacity and limits' },
  catering: { label: 'Catering Services', icon: UtensilsCrossed, description: 'Food and beverage options' },
  setup: { label: 'Setup Options', icon: Layout, description: 'Room and space configuration options' },
  payments: { label: 'Payment Processing', icon: CreditCard, description: 'Secure payment handling' },
  notifications: { label: 'Smart Notifications', icon: MessageCircle, description: 'Automated guest communications' },
  analytics: { label: 'Advanced Analytics', icon: TrendingUp, description: 'Detailed performance insights' }
};

const BOOKING_CATEGORIES = {
  accommodation: [
    { value: 'hotel', label: 'Hotel', description: 'Full-service hotel accommodations' },
    { value: 'bnb', label: 'Bed & Breakfast', description: 'Intimate B&B experiences' },
    { value: 'vacation-rental', label: 'Vacation Rental', description: 'Short-term vacation properties' },
    { value: 'hostel', label: 'Hostel', description: 'Budget-friendly shared accommodations' },
    { value: 'resort', label: 'Resort', description: 'All-inclusive resort experiences' },
    { value: 'camping', label: 'Camping', description: 'Outdoor camping and glamping' },
    { value: 'apartment', label: 'Serviced Apartment', description: 'Extended stay apartments' }
  ],
  appointment: [
    { value: 'medical', label: 'Medical', description: 'Healthcare and medical services' },
    { value: 'beauty', label: 'Beauty & Wellness', description: 'Spa, salon, and wellness services' },
    { value: 'fitness', label: 'Fitness', description: 'Personal training and fitness sessions' },
    { value: 'consultation', label: 'Professional Consultation', description: 'Business and legal consultations' },
    { value: 'therapy', label: 'Therapy', description: 'Mental health and counseling services' },
    { value: 'education', label: 'Education & Tutoring', description: 'Learning and educational services' },
    { value: 'maintenance', label: 'Home Services', description: 'Repair and maintenance services' }
  ],
  rental: [
    { value: 'vehicle', label: 'Vehicle Rental', description: 'Cars, trucks, and motorcycles' },
    { value: 'equipment', label: 'Equipment Rental', description: 'Tools and machinery' },
    { value: 'electronics', label: 'Electronics', description: 'Tech equipment and devices' },
    { value: 'sports', label: 'Sports Equipment', description: 'Athletic and recreational gear' },
    { value: 'party', label: 'Party & Events', description: 'Event supplies and decorations' },
    { value: 'outdoor', label: 'Outdoor Gear', description: 'Camping and adventure equipment' },
    { value: 'musical', label: 'Musical Instruments', description: 'Instruments and audio equipment' }
  ],
  venue: [
    { value: 'meeting-room', label: 'Meeting Room', description: 'Business meeting spaces' },
    { value: 'event-hall', label: 'Event Hall', description: 'Large event and conference spaces' },
    { value: 'wedding-venue', label: 'Wedding Venue', description: 'Wedding ceremony and reception venues' },
    { value: 'studio', label: 'Studio Space', description: 'Photo, video, and creative studios' },
    { value: 'coworking', label: 'Coworking Space', description: 'Flexible office and desk spaces' },
    { value: 'kitchen', label: 'Commercial Kitchen', description: 'Professional cooking facilities' },
    { value: 'warehouse', label: 'Warehouse', description: 'Storage and industrial spaces' }
  ]
};

export const BookingSettings = ({ book, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    // Basic Information
    botName: '',
    description: '',
    bookingType: 'accommodation',
    businessModel: 'single',
    category: 'general',
    
    // Business Information
    businessName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    address: '',
    businessHours: {
      monday: { open: '09:00', close: '17:00', enabled: true },
      tuesday: { open: '09:00', close: '17:00', enabled: true },
      wednesday: { open: '09:00', close: '17:00', enabled: true },
      thursday: { open: '09:00', close: '17:00', enabled: true },
      friday: { open: '09:00', close: '17:00', enabled: true },
      saturday: { open: '09:00', close: '15:00', enabled: true },
      sunday: { open: '10:00', close: '14:00', enabled: false }
    },
    
    // Features & Functionality
    enabledFeatures: {},
    
    // Booking Configuration
    requireLogin: false,
    autoConfirm: true,
    allowCancellation: true,
    cancellationPolicy: '24-hours',
    timeSlotDuration: 60,
    bufferTime: 15,
    advanceBooking: 30,
    instantBooking: false,
    
    // Payment Settings
    requirePayment: false,
    paymentMethods: ['card', 'paypal'],
    depositRequired: false,
    depositPercentage: 20,
    currency: 'USD',
    
    // Multi-vendor Settings
    allowVendorRegistration: false,
    vendorCommission: 10,
    requireVendorApproval: true,
    vendorAutoApproval: false,
    
    // Design & Branding
    logoUrl: '',
    faviconUrl: '',
    heroImage: '',
    primaryColor: '#10b981',
    secondaryColor: '#064e3b',
    backgroundColor: '#ffffff',
    headerColor: '#ffffff',
    headerTextColor: '#1f2937',
    accentColor: '#f59e0b',
    fontFamily: 'Inter, sans-serif',
    borderRadius: 'medium',
    shadowLevel: 'medium',
    
    // Layout & Display
    layout: 'grid',
    itemsPerPage: 12,
    showHero: true,
    showFilters: true,
    showMap: false,
    showReviews: true,
    cardStyle: 'modern',

    pricingModel: 'per-listing', 
    standardPrice: '',
    pricingUnit: 'night',
    weekendPremium: false,
    weekendMultiplier: 1.2,
    
    // SEO & Domain
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    ogImage: '',
    enableStructuredData: true,
    allowIndexing: true,
    
    // Privacy & Access
    accessLevel: 'public',
    allowAnonymous: true,
    requireLoginForBooking: false,
    moderateReviews: true,
    published: false,

    authorizedEmails: [],
    accessPassword: '',
    
    // Notifications
    emailNotifications: true,
    smsNotifications: false,
    bookingConfirmations: true,
    reminderEmails: true,
    reviewRequests: true,
    
    // Analytics & Tracking
    enableAnalytics: true,
    googleAnalytics: '',
    facebookPixel: '',
    enableHeatmaps: false,
    trackConversions: true,
    
    // Integrations
    googleMapsApiKey: '',
    whatsappNumber: '',
    calendarSync: false,
    zapierWebhook: '',
    slackWebhook: '',
    
    // Advanced Settings
    customCSS: '',
    customJS: '',
    enableAPI: false,
    apiKey: '',
    webhookUrl: '',
    enableBackups: true,
    maintenanceMode: false
  });

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Type },
    { id: 'business', label: 'Business Details', icon: Building2 },
    { id: 'features', label: 'Features', icon: Zap },
    { id: 'booking', label: 'Booking Rules', icon: Calendar },
    { id: 'payment', label: 'Payments', icon: CreditCard },
    { id: 'vendors', label: 'Multi-Vendor', icon: Users },
    { id: 'design', label: 'Design & Branding', icon: Palette },
    { id: 'layout', label: 'Layout & Display', icon: Layout },
    { id: 'seo', label: 'SEO & Domain', icon: Globe },
    { id: 'privacy', label: 'Privacy & Access', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Mail },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'integrations', label: 'Integrations', icon: Settings },
    { id: 'advanced', label: 'Advanced', icon: Code },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  useEffect(() => {
    if (book) {
      const bookingSettings = book.bookingSettings || {};
      
      setFormData({
        // Initialize from book data with fallbacks
        botName: book.botName || '',
        description: book.description || '',
        bookingType: bookingSettings.bookingType || 'accommodation',
        businessModel: bookingSettings.businessModel || 'single',
        category: bookingSettings.category || 'general',
        
        businessName: bookingSettings.businessName || '',
        contactEmail: bookingSettings.contactEmail || '',
        contactPhone: bookingSettings.contactPhone || '',
        website: bookingSettings.website || '',
        address: bookingSettings.address || '',
        businessHours: bookingSettings.businessHours || {
          monday: { open: '09:00', close: '17:00', enabled: true },
          tuesday: { open: '09:00', close: '17:00', enabled: true },
          wednesday: { open: '09:00', close: '17:00', enabled: true },
          thursday: { open: '09:00', close: '17:00', enabled: true },
          friday: { open: '09:00', close: '17:00', enabled: true },
          saturday: { open: '09:00', close: '15:00', enabled: true },
          sunday: { open: '10:00', close: '14:00', enabled: false }
        },
        
        enabledFeatures: bookingSettings.enabledFeatures || {},
        
        requireLogin: bookingSettings.requireLogin || false,
        autoConfirm: bookingSettings.autoConfirm !== false,
        allowCancellation: bookingSettings.allowCancellation !== false,
        cancellationPolicy: bookingSettings.cancellationPolicy || '24-hours',
        timeSlotDuration: bookingSettings.timeSlotDuration || 60,
        bufferTime: bookingSettings.bufferTime || 15,
        advanceBooking: bookingSettings.advanceBooking || 30,
        instantBooking: bookingSettings.instantBooking || false,
        
        requirePayment: bookingSettings.requirePayment || false,
        paymentMethods: bookingSettings.paymentMethods || ['card', 'paypal'],
        depositRequired: bookingSettings.depositRequired || false,
        depositPercentage: bookingSettings.depositPercentage || 20,
        currency: bookingSettings.currency || 'USD',
        
        allowVendorRegistration: bookingSettings.allowVendorRegistration || false,
        vendorCommission: bookingSettings.vendorCommission || 10,
        requireVendorApproval: bookingSettings.requireVendorApproval !== false,
        vendorAutoApproval: bookingSettings.vendorAutoApproval || false,
        
        logoUrl: book.logoUrl || '',
        faviconUrl: book.faviconUrl || '',
        heroImage: bookingSettings.heroImage || '',
        primaryColor: book.styling?.primaryColor || bookingSettings.primaryColor || '#10b981',
        secondaryColor: book.styling?.secondaryColor || bookingSettings.secondaryColor || '#064e3b',
        backgroundColor: book.styling?.backgroundColor || bookingSettings.backgroundColor || '#ffffff',
        headerColor: book.styling?.headerColor || bookingSettings.headerColor || '#ffffff',
        headerTextColor: book.styling?.headerTextColor || bookingSettings.headerTextColor || '#1f2937',
        accentColor: book.styling?.accentColor || bookingSettings.accentColor || '#f59e0b',
        fontFamily: book.styling?.fontFamily || bookingSettings.fontFamily || 'Inter, sans-serif',
        borderRadius: bookingSettings.borderRadius || 'medium',
        shadowLevel: bookingSettings.shadowLevel || 'medium',
        
        layout: bookingSettings.layout || 'grid',
        itemsPerPage: bookingSettings.itemsPerPage || 12,
        showHero: bookingSettings.showHero !== false,
        showFilters: bookingSettings.showFilters !== false,
        showMap: bookingSettings.showMap || false,
        showReviews: bookingSettings.showReviews !== false,
        cardStyle: bookingSettings.cardStyle || 'modern',
        
        customDomain: book.customDomain || '',
        customPath: book.customPath || '',
        seoTitle: book.seoTitle || '',
        seoDescription: book.seoDescription || '',
        seoKeywords: book.seoKeywords || '',
        ogImage: book.ogImage || '',
        enableStructuredData: bookingSettings.enableStructuredData !== false,
        allowIndexing: book.allowIndexing !== false,
        
        accessLevel: book.accessLevel || 'public',
        allowAnonymous: bookingSettings.allowAnonymous !== false,
        requireLoginForBooking: bookingSettings.requireLoginForBooking || false,
        moderateReviews: bookingSettings.moderateReviews !== false,
        published: book.published || false,
        
        emailNotifications: bookingSettings.emailNotifications !== false,
        smsNotifications: bookingSettings.smsNotifications || false,
        bookingConfirmations: bookingSettings.bookingConfirmations !== false,
        reminderEmails: bookingSettings.reminderEmails !== false,
        reviewRequests: bookingSettings.reviewRequests !== false,
        
        enableAnalytics: bookingSettings.enableAnalytics !== false,
        googleAnalytics: bookingSettings.googleAnalytics || '',
        facebookPixel: bookingSettings.facebookPixel || '',
        enableHeatmaps: bookingSettings.enableHeatmaps || false,
        trackConversions: bookingSettings.trackConversions !== false,
        
        googleMapsApiKey: bookingSettings.googleMapsApiKey || '',
        whatsappNumber: bookingSettings.whatsappNumber || '',
        calendarSync: bookingSettings.calendarSync || false,
        zapierWebhook: bookingSettings.zapierWebhook || '',
        slackWebhook: bookingSettings.slackWebhook || '',
        
        customCSS: book.customCSS || '',
        customJS: bookingSettings.customJS || '',
        enableAPI: bookingSettings.enableAPI || false,
        apiKey: bookingSettings.apiKey || '',
        webhookUrl: bookingSettings.webhookUrl || '',
        enableBackups: bookingSettings.enableBackups !== false,
        maintenanceMode: bookingSettings.maintenanceMode || false
      });
    }
  }, [book]);

  const handleSave = async () => {
    setSaving(true);
    setValidationErrors({});
    
    try {
      // Validation
      const errors = {};
      
      if (!formData.botName?.trim()) {
        errors.botName = 'Business name is required';
      }
      
      if (!formData.description?.trim()) {
        errors.description = 'Description is required';
      }
      
      if (formData.customDomain && !isValidDomain(formData.customDomain)) {
        errors.customDomain = 'Please enter a valid domain name';
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setSaving(false);
        return;
      }

      // Prepare update data
      const updatedBook = {
        ...book,
        botName: formData.botName,
        description: formData.description,
        templateType: 'booking',
        
        // Update main book fields
        logoUrl: formData.logoUrl,
        faviconUrl: formData.faviconUrl,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        ogImage: formData.ogImage,
        allowIndexing: formData.allowIndexing,
        accessLevel: formData.accessLevel,
        published: formData.published,
        customCSS: formData.customCSS,
        
        // Update styling
        styling: {
          ...book.styling,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily
        },
        
        // Update booking settings
        bookingSettings: {
          bookingType: formData.bookingType,
          businessModel: formData.businessModel,
          category: formData.category,
          
          businessName: formData.businessName,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          website: formData.website,
          address: formData.address,
          businessHours: formData.businessHours,
          
          enabledFeatures: formData.enabledFeatures,
          
          requireLogin: formData.requireLogin,
          autoConfirm: formData.autoConfirm,
          allowCancellation: formData.allowCancellation,
          cancellationPolicy: formData.cancellationPolicy,
          timeSlotDuration: formData.timeSlotDuration,
          bufferTime: formData.bufferTime,
          advanceBooking: formData.advanceBooking,
          instantBooking: formData.instantBooking,
          
          requirePayment: formData.requirePayment,
          paymentMethods: formData.paymentMethods,
          depositRequired: formData.depositRequired,
          depositPercentage: formData.depositPercentage,
          currency: formData.currency,
          
          allowVendorRegistration: formData.allowVendorRegistration,
          vendorCommission: formData.vendorCommission,
          requireVendorApproval: formData.requireVendorApproval,
          vendorAutoApproval: formData.vendorAutoApproval,
          
          heroImage: formData.heroImage,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          borderRadius: formData.borderRadius,
          shadowLevel: formData.shadowLevel,
          
          layout: formData.layout,
          itemsPerPage: formData.itemsPerPage,
          showHero: formData.showHero,
          showFilters: formData.showFilters,
          showMap: formData.showMap,
          showReviews: formData.showReviews,
          cardStyle: formData.cardStyle,
          
          enableStructuredData: formData.enableStructuredData,
          allowAnonymous: formData.allowAnonymous,
          requireLoginForBooking: formData.requireLoginForBooking,
          moderateReviews: formData.moderateReviews,
          
          emailNotifications: formData.emailNotifications,
          smsNotifications: formData.smsNotifications,
          bookingConfirmations: formData.bookingConfirmations,
          reminderEmails: formData.reminderEmails,
          reviewRequests: formData.reviewRequests,
          
          enableAnalytics: formData.enableAnalytics,
          googleAnalytics: formData.googleAnalytics,
          facebookPixel: formData.facebookPixel,
          enableHeatmaps: formData.enableHeatmaps,
          trackConversions: formData.trackConversions,
          
          googleMapsApiKey: formData.googleMapsApiKey,
          whatsappNumber: formData.whatsappNumber,
          calendarSync: formData.calendarSync,
          zapierWebhook: formData.zapierWebhook,
          slackWebhook: formData.slackWebhook,
          
          customJS: formData.customJS,
          enableAPI: formData.enableAPI,
          apiKey: formData.apiKey,
          webhookUrl: formData.webhookUrl,
          enableBackups: formData.enableBackups,
          maintenanceMode: formData.maintenanceMode
        },
        
        updatedAt: new Date()
      };

      await firestoreService.updateBook(book.id, updatedBook);
      onUpdate(updatedBook);
      onClose();
      
    } catch (error) {
      console.error('Error saving booking settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBookingTypeChange = (newType) => {
    const typeConfig = BOOKING_TYPES[newType];
    setFormData(prev => ({
      ...prev,
      bookingType: newType,
      primaryColor: typeConfig.color,
      enabledFeatures: {
        ...prev.enabledFeatures,
        ...typeConfig.defaultFeatures.reduce((acc, feature) => {
          acc[feature] = true;
          return acc;
        }, {})
      }
    }));
  };

  const handleFeatureToggle = (featureKey, enabled) => {
    setFormData(prev => ({
      ...prev,
      enabledFeatures: {
        ...prev.enabledFeatures,
        [featureKey]: enabled
      }
    }));
  };

  // File upload handlers
  const handleFileUpload = async (file, fileType) => {
    if (!file) return;

    setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));

    try {
      const result = await firestoreService.uploadFile(file, `pb-${book.id}/bookings/${fileType}`);
      
      setFormData(prev => ({
        ...prev,
        [fileType === 'logo' ? 'logoUrl' : 
         fileType === 'favicon' ? 'faviconUrl' : 
         fileType === 'hero' ? 'heroImage' : 
         fileType === 'ogImage' ? 'ogImage' : fileType]: result.url
      }));

      setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setUploadStates(prev => ({ 
        ...prev, 
        [fileType]: { uploading: false, error: error.message } 
      }));
    }
  };

  const handleLogoUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, logo: error }));
      return;
    }
    handleFileUpload(file, 'logo');
  };

  const handleFaviconUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, favicon: error }));
      return;
    }
    handleFileUpload(file, 'favicon');
  };

  const handleHeroUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, hero: error }));
      return;
    }
    handleFileUpload(file, 'hero');
  };

  const handleOgImageUpload = (file, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, ogImage: error }));
      return;
    }
    handleFileUpload(file, 'ogImage');
  };

  const isValidDomain = (domain) => {
    const domainRegex = /^(?!-)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };


  const generatePreviewUrl = () => {
    const domain = formData.customDomain.trim();
    const path = formData.customPath.trim();
    
    if (!domain) return '';
    
    if (path) {
      return `https://${domain}/${path}`;
    }
    return `https://${domain}`;
  };

  if (!isOpen) return null;

  const currentBookingType = BOOKING_TYPES[formData.bookingType];
  const currentBusinessModel = BUSINESS_MODELS[formData.businessModel];

  const availableCategories = BOOKING_CATEGORIES[formData.bookingType] || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 text-white"
          style={{ backgroundColor: currentBookingType.color }}
        >
          <div className="flex items-center gap-3">
            {React.createElement(currentBookingType.icon, { className: "h-6 w-6" })}
            <div>
              <h2 className="text-xl font-semibold">Booking Settings</h2>
              <p className="text-white/80 text-sm">
                {currentBookingType.label} • {currentBusinessModel.label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Tabs */}
          <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-4">
              <nav className="space-y-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                      {tab.id === 'features' && Object.keys(formData.enabledFeatures).length > 0 && (
                        <span className="ml-auto bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {Object.values(formData.enabledFeatures).filter(Boolean).length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-8">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <BasicInfoTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleBookingTypeChange={handleBookingTypeChange}
                  validationErrors={validationErrors}
                  currentBookingType={currentBookingType}
                  currentBusinessModel={currentBusinessModel}
                  availableCategories={availableCategories}
                />
              )}

              {/* Business Details Tab */}
              {activeTab === 'business' && (
                <BusinessDetailsTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  validationErrors={validationErrors}
                />
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <FeaturesTab 
                  formData={formData}
                  handleFeatureToggle={handleFeatureToggle}
                  currentBookingType={currentBookingType}
                />
              )}

              {/* Booking Rules Tab */}
              {activeTab === 'booking' && (
                <BookingRulesTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Payment Tab */}
              {activeTab === 'payment' && (
                <PaymentTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Multi-Vendor Tab */}
              {activeTab === 'vendors' && (
                <MultiVendorTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Design & Branding Tab */}
              {activeTab === 'design' && (
                <DesignBrandingTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleLogoUpload={handleLogoUpload}
                  handleFaviconUpload={handleFaviconUpload}
                  handleHeroUpload={handleHeroUpload}
                  uploadStates={uploadStates}
                  validationErrors={validationErrors}
                />
              )}

              {/* Layout & Display Tab */}
              {activeTab === 'layout' && (
                <LayoutDisplayTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* SEO & Domain Tab */}
              {activeTab === 'seo' && (
                <SEODomainTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOgImageUpload={handleOgImageUpload}
                  uploadStates={uploadStates}
                  validationErrors={validationErrors}
                  generatePreviewUrl={generatePreviewUrl}
                />
              )}

              {/* Privacy & Access Tab */}
              {activeTab === 'privacy' && (
                <PrivacyAccessTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <NotificationsTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Integrations Tab */}
              {activeTab === 'integrations' && (
                <IntegrationsTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Advanced Tab */}
              {activeTab === 'advanced' && (
                <AdvancedTab 
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <PreviewTab 
                  formData={formData}
                  previewMode={previewMode}
                  setPreviewMode={setPreviewMode}
                  generatePreviewUrl={generatePreviewUrl}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Basic Info Tab Component
const BasicInfoTab = ({ 
  formData, 
  handleInputChange, 
  handleBookingTypeChange, 
  validationErrors, 
  currentBookingType,
  currentBusinessModel,
  availableCategories 
}) => (
  
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic Information</h3>
      <p className="text-gray-600">
        Configure the fundamental settings for your booking system
      </p>
    </div>

    {/* Booking Type Selection */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-4">
        Booking Type
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(BOOKING_TYPES).map(([key, type]) => {
          const TypeIcon = type.icon;
          return (
            <div
              key={key}
              onClick={() => handleBookingTypeChange(key)}
              className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                formData.bookingType === key
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div 
                  className="p-3 rounded-lg"
                  style={{ 
                    backgroundColor: formData.bookingType === key ? type.color : '#f3f4f6',
                    color: formData.bookingType === key ? 'white' : '#6b7280'
                  }}
                >
                  <TypeIcon className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{type.label}</h4>
                  <p className="text-sm text-gray-600">{type.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {type.defaultFeatures.slice(0, 3).map(feature => (
                      <span key={feature} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Business Model Selection */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-4">
        Business Model
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(BUSINESS_MODELS).map(([key, model]) => {
          const ModelIcon = model.icon;
          return (
            <div
              key={key}
              onClick={() => handleInputChange('businessModel', key)}
              className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                formData.businessModel === key
                  ? 'border-emerald-500 bg-emerald-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  formData.businessModel === key 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <ModelIcon className="h-6 w-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{model.label}</h4>
                  <p className="text-sm text-gray-600">{model.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Basic Form Fields */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Name *
        </label>
        <input
          type="text"
          value={formData.botName}
          onChange={(e) => handleInputChange('botName', e.target.value)}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            validationErrors.botName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter your business name"
        />
        {validationErrors.botName && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.botName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Category
        </label>
        <p className="text-sm text-gray-600 mb-4">
          Choose the specific category that best describes your {currentBookingType?.label.toLowerCase()}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {availableCategories.map((category) => (
            <div
              key={category.value}
              onClick={() => handleInputChange('category', category.value)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.category === category.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${
                  formData.category === category.value
                    ? 'bg-blue-500'
                    : 'bg-gray-300'
                }`} />
                <h4 className="font-medium text-gray-900">{category.label}</h4>
              </div>
              <p className="text-sm text-gray-600">{category.description}</p>
            </div>
          ))}
        </div>

        {formData.category && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-700">
                Category selected: {availableCategories.find(c => c.value === formData.category)?.label}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Description *
      </label>
      <textarea
        value={formData.description}
        onChange={(e) => handleInputChange('description', e.target.value)}
        rows={4}
        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          validationErrors.description ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder="Describe your booking business and what makes it special"
      />
      {validationErrors.description && (
        <p className="text-red-500 text-sm mt-1">{validationErrors.description}</p>
      )}
      <p className="text-sm text-gray-500 mt-1">
        {formData.description.length}/500 characters
      </p>
    </div>

    {/* Current Configuration Summary */}
    <div className="bg-gray-50 p-6 rounded-lg">
      <h4 className="font-semibold text-gray-900 mb-4">Configuration Summary</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Type:</span>
          <span className="ml-2 font-medium">{currentBookingType.label}</span>
        </div>
        <div>
          <span className="text-gray-600">Model:</span>
          <span className="ml-2 font-medium">{currentBusinessModel.label}</span>
        </div>
        <div>
          <span className="text-gray-600">Features:</span>
          <span className="ml-2 font-medium">
            {Object.values(formData.enabledFeatures).filter(Boolean).length} enabled
          </span>
        </div>
      </div>
    </div>
  </div>
);

// Business Details Tab Component
const BusinessDetailsTab = ({ formData, handleInputChange, validationErrors }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Details</h3>
      <p className="text-gray-600">
        Configure your business contact information and operating hours
      </p>
    </div>

    {/* Contact Information */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="h-4 w-4 inline mr-1" />
            Contact Email
          </label>
          <input
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="contact@yourbusiness.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="h-4 w-4 inline mr-1" />
            Contact Phone
          </label>
          <input
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Globe className="h-4 w-4 inline mr-1" />
            Website
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="https://yourbusiness.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <MapPin className="h-4 w-4 inline mr-1" />
            Business Address
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="123 Business St, City, Country"
          />
        </div>
      </div>
    </div>

    {/* Business Hours */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Business Hours</h4>
      <div className="space-y-4">
        {Object.entries(formData.businessHours).map(([day, hours]) => (
          <div key={day} className="flex items-center gap-4">
            <div className="w-24">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={hours.enabled}
                  onChange={(e) => handleInputChange('businessHours', {
                    ...formData.businessHours,
                    [day]: { ...hours, enabled: e.target.checked }
                  })}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="capitalize text-sm font-medium text-gray-700">{day}</span>
              </label>
            </div>
            
            {hours.enabled && (
              <>
                <input
                  type="time"
                  value={hours.open}
                  onChange={(e) => handleInputChange('businessHours', {
                    ...formData.businessHours,
                    [day]: { ...hours, open: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="time"
                  value={hours.close}
                  onChange={(e) => handleInputChange('businessHours', {
                    ...formData.businessHours,
                    [day]: { ...hours, close: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </>
            )}
            
            {!hours.enabled && (
              <span className="text-gray-500 text-sm">Closed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Features Tab Component
const FeaturesTab = ({ formData, handleFeatureToggle, currentBookingType }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Features & Functionality</h3>
      <p className="text-gray-600">
        Enable the features that best suit your {currentBookingType.label.toLowerCase()} business
      </p>
    </div>

    {/* Recommended Features */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        Recommended for {currentBookingType.label}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentBookingType.defaultFeatures.map(featureKey => {
          const feature = BOOKING_FEATURES[featureKey];
          if (!feature) return null;
          
          const FeatureIcon = feature.icon;
          const isEnabled = formData.enabledFeatures[featureKey];
          
          return (
            <div
              key={featureKey}
              onClick={() => handleFeatureToggle(featureKey, !isEnabled)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isEnabled
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  isEnabled ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <FeatureIcon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900">{feature.label}</h5>
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      isEnabled ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {isEnabled && (
                        <CheckCircle className="w-3 h-3 text-white m-0.5" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* All Available Features */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">All Available Features</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(BOOKING_FEATURES).map(([featureKey, feature]) => {
          const FeatureIcon = feature.icon;
          const isEnabled = formData.enabledFeatures[featureKey];
          const isRecommended = currentBookingType.defaultFeatures.includes(featureKey);
          
          return (
            <div
              key={featureKey}
              onClick={() => handleFeatureToggle(featureKey, !isEnabled)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all relative ${
                isEnabled
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {isRecommended && (
                <div className="absolute top-2 right-2">
                  <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                    Recommended
                  </span>
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  isEnabled ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <FeatureIcon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900">{feature.label}</h5>
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      isEnabled ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {isEnabled && (
                        <CheckCircle className="w-3 h-3 text-white m-0.5" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Feature Summary */}
    <div className="bg-blue-50 p-6 rounded-lg">
      <h4 className="font-semibold text-blue-900 mb-2">Feature Summary</h4>
      <p className="text-blue-700 text-sm">
        You have enabled {Object.values(formData.enabledFeatures).filter(Boolean).length} out of {Object.keys(BOOKING_FEATURES).length} available features.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.entries(formData.enabledFeatures)
          .filter(([_, enabled]) => enabled)
          .map(([featureKey, _]) => (
            <span key={featureKey} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              {BOOKING_FEATURES[featureKey]?.label}
            </span>
          ))}
      </div>
    </div>
  </div>
);

// Preview Tab Component  
const PreviewTab = ({ formData, previewMode, setPreviewMode, generatePreviewUrl }) => {
  const previewUrl = generatePreviewUrl();

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Preview</h3>
        <p className="text-gray-600">
          See how your booking system will look to customers
        </p>
      </div>

      {/* Preview Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[
            { id: 'desktop', label: 'Desktop', icon: Monitor },
            { id: 'tablet', label: 'Tablet', icon: Tablet },
            { id: 'mobile', label: 'Mobile', icon: Smartphone }
          ].map(device => {
            const DeviceIcon = device.icon;
            return (
              <button
                key={device.id}
                onClick={() => setPreviewMode(device.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  previewMode === device.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <DeviceIcon className="h-4 w-4" />
                {device.label}
              </button>
            );
          })}
        </div>

        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <ExternalLink className="h-4 w-4" />
            Open Live Preview
          </a>
        )}
      </div>

      {/* Preview Frame */}
      <div className="bg-gray-100 p-8 rounded-lg">
        <div 
          className={`mx-auto bg-white border border-gray-300 rounded-lg overflow-hidden ${
            previewMode === 'desktop' ? 'w-full max-w-6xl h-96' :
            previewMode === 'tablet' ? 'w-full max-w-2xl h-96' :
            'w-full max-w-sm h-96'
          }`}
        >
          <div 
            className="h-full flex flex-col"
            style={{
              backgroundColor: formData.backgroundColor,
              fontFamily: formData.fontFamily,
              color: formData.headerTextColor
            }}
          >
            {/* Preview Header */}
            <div 
              className="p-4 border-b"
              style={{ 
                backgroundColor: formData.headerColor,
                color: formData.headerTextColor 
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {formData.logoUrl && (
                    <img src={formData.logoUrl} alt="Logo" className="h-8 w-8 object-cover rounded" />
                  )}
                  <h1 className="text-lg font-semibold">{formData.botName || 'Your Business'}</h1>
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1 rounded text-sm"
                    style={{ 
                      backgroundColor: formData.primaryColor,
                      color: 'white'
                    }}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 p-4">
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Welcome to {formData.botName}</h2>
                  <p className="text-gray-600">{formData.description || 'Your business description will appear here.'}</p>
                </div>

                {/* Sample Booking Cards */}
                <div className={`grid gap-4 ${
                  formData.layout === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
                }`}>
                  {[1, 2].map(i => (
                    <div 
                      key={i} 
                      className="border rounded-lg p-4"
                      style={{ 
                        borderRadius: formData.borderRadius === 'small' ? '4px' : 
                                    formData.borderRadius === 'large' ? '16px' : '8px'
                      }}
                    >
                      <div className="bg-gray-200 h-32 rounded mb-3"></div>
                      <h3 className="font-medium">Sample {BOOKING_TYPES[formData.bookingType].label}</h3>
                      <p className="text-sm text-gray-600">Sample description text</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-semibold" style={{ color: formData.primaryColor }}>
                          ${formData.currency} 99
                        </span>
                        <button 
                          className="px-3 py-1 rounded text-sm"
                          style={{ 
                            backgroundColor: formData.primaryColor,
                            color: 'white'
                          }}
                        >
                          Book
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Summary */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-4">Preview Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium">{BOOKING_TYPES[formData.bookingType].label}</span>
          </div>
          <div>
            <span className="text-gray-600">Layout:</span>
            <span className="ml-2 font-medium capitalize">{formData.layout}</span>
          </div>
          <div>
            <span className="text-gray-600">Theme:</span>
            <span className="ml-2 font-medium">{formData.primaryColor}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Continue adding these tab components to BookingSettings.jsx

// Design & Branding Tab Component
const DesignBrandingTab = ({ 
  formData, 
  handleInputChange, 
  handleLogoUpload, 
  handleFaviconUpload, 
  handleHeroUpload,
  uploadStates, 
  validationErrors 
}) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Design & Branding</h3>
      <p className="text-gray-600">
        Customize the visual appearance and branding of your booking system
      </p>
    </div>

    {/* Logo & Visual Assets */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Visual Assets</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Logo
          </label>
          <FileUpload
            onFileSelect={handleLogoUpload}
            currentFile={formData.logoUrl}
            onRemove={() => handleInputChange('logoUrl', '')}
            accept="image/*"
            maxSize={5}
            label="Upload Logo"
            uploading={uploadStates.logo?.uploading || false}
            error={uploadStates.logo?.error || validationErrors.logo}
            showPreview={true}
          />
          <p className="text-xs text-gray-500 mt-1">
            Recommended: 200x200px, PNG or JPG
          </p>
        </div>

        {/* Favicon Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Favicon
          </label>
          <FileUpload
            onFileSelect={handleFaviconUpload}
            currentFile={formData.faviconUrl}
            onRemove={() => handleInputChange('faviconUrl', '')}
            accept="image/*"
            maxSize={1}
            label="Upload Favicon"
            uploading={uploadStates.favicon?.uploading || false}
            error={uploadStates.favicon?.error || validationErrors.favicon}
            showPreview={true}
          />
          <p className="text-xs text-gray-500 mt-1">
            Recommended: 32x32px, PNG or ICO
          </p>
        </div>

        {/* Hero Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hero Image
          </label>
          <FileUpload
            onFileSelect={handleHeroUpload}
            currentFile={formData.heroImage}
            onRemove={() => handleInputChange('heroImage', '')}
            accept="image/*"
            maxSize={10}
            label="Upload Hero Image"
            uploading={uploadStates.hero?.uploading || false}
            error={uploadStates.hero?.error || validationErrors.hero}
            showPreview={true}
          />
          <p className="text-xs text-gray-500 mt-1">
            Recommended: 1920x600px, JPG or PNG
          </p>
        </div>
      </div>
    </div>

    {/* Color Scheme */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Color Scheme</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.primaryColor}
              onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.primaryColor}
              onChange={(e) => handleInputChange('primaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secondary Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.secondaryColor}
              onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.secondaryColor}
              onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accent Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.accentColor}
              onChange={(e) => handleInputChange('accentColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.accentColor}
              onChange={(e) => handleInputChange('accentColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#000000"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Background Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.backgroundColor}
              onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.backgroundColor}
              onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#ffffff"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Header Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.headerColor}
              onChange={(e) => handleInputChange('headerColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.headerColor}
              onChange={(e) => handleInputChange('headerColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#ffffff"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Header Text Color
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              value={formData.headerTextColor}
              onChange={(e) => handleInputChange('headerTextColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={formData.headerTextColor}
              onChange={(e) => handleInputChange('headerTextColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="#1f2937"
            />
          </div>
        </div>
      </div>
    </div>

    {/* Typography */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Typography</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Font Family
          </label>
          <select
            value={formData.fontFamily}
            onChange={(e) => handleInputChange('fontFamily', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="Inter, sans-serif">Inter (Modern)</option>
            <option value="system-ui, sans-serif">System UI</option>
            <option value="Roboto, sans-serif">Roboto</option>
            <option value="Poppins, sans-serif">Poppins</option>
            <option value="Montserrat, sans-serif">Montserrat</option>
            <option value="Playfair Display, serif">Playfair Display (Elegant)</option>
            <option value="Georgia, serif">Georgia (Classic)</option>
            <option value="Times New Roman, serif">Times New Roman</option>
          </select>
        </div>
      </div>
    </div>

    {/* Design Elements */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Design Elements</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Border Radius
          </label>
          <select
            value={formData.borderRadius}
            onChange={(e) => handleInputChange('borderRadius', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="none">None (Square)</option>
            <option value="small">Small (4px)</option>
            <option value="medium">Medium (8px)</option>
            <option value="large">Large (16px)</option>
            <option value="extra-large">Extra Large (24px)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Shadow Level
          </label>
          <select
            value={formData.shadowLevel}
            onChange={(e) => handleInputChange('shadowLevel', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
            <option value="extra-large">Extra Large</option>
          </select>
        </div>
      </div>
    </div>

    {/* Live Preview */}
    <div className="bg-gray-50 p-6 rounded-lg">
      <h4 className="font-semibold text-gray-900 mb-4">Live Preview</h4>
      <div 
        className="p-6 rounded-lg border"
        style={{
          backgroundColor: formData.backgroundColor,
          fontFamily: formData.fontFamily,
          borderRadius: formData.borderRadius === 'small' ? '4px' : 
                        formData.borderRadius === 'large' ? '16px' : 
                        formData.borderRadius === 'extra-large' ? '24px' : '8px'
        }}
      >
        <div 
          className="p-4 rounded mb-4"
          style={{ 
            backgroundColor: formData.headerColor,
            color: formData.headerTextColor 
          }}
        >
          <h3 className="text-lg font-semibold">Sample Header</h3>
        </div>
        <button 
          className="px-4 py-2 rounded text-white font-medium"
          style={{ backgroundColor: formData.primaryColor }}
        >
          Primary Button
        </button>
        <button 
          className="ml-3 px-4 py-2 rounded text-white font-medium"
          style={{ backgroundColor: formData.accentColor }}
        >
          Accent Button
        </button>
      </div>
    </div>
  </div>
);

// Layout & Display Tab Component
const LayoutDisplayTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Layout & Display</h3>
      <p className="text-gray-600">
        Configure how your bookings are displayed to customers
      </p>
    </div>

    {/* Layout Options */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Layout Style</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { id: 'grid', label: 'Grid Layout', description: 'Cards in a responsive grid' },
          { id: 'list', label: 'List Layout', description: 'Detailed list view' },
          { id: 'masonry', label: 'Masonry Layout', description: 'Pinterest-style layout' }
        ].map(layout => (
          <div
            key={layout.id}
            onClick={() => handleInputChange('layout', layout.id)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.layout === layout.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h5 className="font-medium text-gray-900">{layout.label}</h5>
            <p className="text-sm text-gray-600">{layout.description}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Card Style */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Card Style</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { id: 'modern', label: 'Modern', description: 'Clean with subtle shadows' },
          { id: 'minimal', label: 'Minimal', description: 'Simple border design' },
          { id: 'elevated', label: 'Elevated', description: 'Strong shadows and depth' }
        ].map(style => (
          <div
            key={style.id}
            onClick={() => handleInputChange('cardStyle', style.id)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.cardStyle === style.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h5 className="font-medium text-gray-900">{style.label}</h5>
            <p className="text-sm text-gray-600">{style.description}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Display Options */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Display Options</h4>
      <div className="space-y-4">
        {[
          { key: 'showHero', label: 'Show Hero Section', description: 'Display hero banner at the top' },
          { key: 'showFilters', label: 'Show Filters', description: 'Enable filtering and search options' },
          { key: 'showMap', label: 'Show Map Integration', description: 'Display location map for listings' },
          { key: 'showReviews', label: 'Show Reviews', description: 'Display customer reviews and ratings' }
        ].map(option => (
          <label key={option.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">{option.label}</span>
              <p className="text-sm text-gray-600">{option.description}</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData[option.key]}
                onChange={(e) => handleInputChange(option.key, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        ))}
      </div>
    </div>

    {/* Pagination Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Pagination</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Items Per Page
          </label>
          <select
            value={formData.itemsPerPage}
            onChange={(e) => handleInputChange('itemsPerPage', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={6}>6 items</option>
            <option value={12}>12 items</option>
            <option value={24}>24 items</option>
            <option value={48}>48 items</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

// Booking Rules Tab Component
const BookingRulesTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Rules</h3>
      <p className="text-gray-600">
        Configure how customers can make bookings and what rules apply
      </p>
    </div>

    {/* Basic Booking Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Settings</h4>
      <div className="space-y-4">
        {[
          { key: 'requireLogin', label: 'Require Login to View', description: 'Users must log in to see listings' },
          { key: 'autoConfirm', label: 'Auto-Confirm Bookings', description: 'Bookings are automatically confirmed' },
          { key: 'allowCancellation', label: 'Allow Cancellations', description: 'Customers can cancel their bookings' },
          { key: 'instantBooking', label: 'Instant Booking', description: 'Enable instant booking without approval' }
        ].map(setting => (
          <label key={setting.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">{setting.label}</span>
              <p className="text-sm text-gray-600">{setting.description}</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData[setting.key]}
                onChange={(e) => handleInputChange(setting.key, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        ))}
      </div>
    </div>

    {/* Cancellation Policy */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Cancellation Policy</h4>
      <div className="space-y-3">
        {[
          { value: 'flexible', label: 'Flexible', description: 'Free cancellation up to 24 hours before' },
          { value: '24-hours', label: '24 Hours', description: 'Free cancellation up to 24 hours before' },
          { value: '48-hours', label: '48 Hours', description: 'Free cancellation up to 48 hours before' },
          { value: '7-days', label: '7 Days', description: 'Free cancellation up to 7 days before' },
          { value: 'strict', label: 'Strict', description: 'No refunds for cancellations' }
        ].map(policy => (
          <div
            key={policy.value}
            onClick={() => handleInputChange('cancellationPolicy', policy.value)}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.cancellationPolicy === policy.value
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 ${
                formData.cancellationPolicy === policy.value
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300'
              }`}>
                {formData.cancellationPolicy === policy.value && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">{policy.label}</div>
                <div className="text-sm text-gray-600">{policy.description}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Time Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Time Settings</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Slot Duration (minutes)
          </label>
          <select
            value={formData.timeSlotDuration}
            onChange={(e) => handleInputChange('timeSlotDuration', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={120}>2 hours</option>
            <option value={240}>4 hours</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buffer Time (minutes)
          </label>
          <select
            value={formData.bufferTime}
            onChange={(e) => handleInputChange('bufferTime', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>No buffer</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Advance Booking (days)
          </label>
          <select
            value={formData.advanceBooking}
            onChange={(e) => handleInputChange('advanceBooking', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={1}>1 day</option>
            <option value={7}>1 week</option>
            <option value={30}>1 month</option>
            <option value={90}>3 months</option>
            <option value={365}>1 year</option>
          </select>
        </div>
      </div>
    </div>
  </div>
);

// Payment Tab Component
const PaymentTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Settings</h3>
      <p className="text-gray-600">
        Configure payment options and pricing settings
      </p>
    </div>
    
    {/* NEW: Single Vendor Pricing Model */}
      {formData.businessModel === 'single' && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Pricing Model</h4>
          <div className="space-y-4">
            {[
              { 
                value: 'per-listing', 
                label: 'Price Per Listing', 
                description: 'Each listing can have its own unique pricing',
                icon: DollarSign
              },
              { 
                value: 'unified-pricing', 
                label: 'Unified Pricing', 
                description: 'One standard price for all listing options',
                icon: CreditCard
              }
            ].map(model => {
              const IconComponent = model.icon;
              return (
                <div
                  key={model.value}
                  onClick={() => handleInputChange('pricingModel', model.value)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.pricingModel === model.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${
                      formData.pricingModel === model.value
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 mb-1">{model.label}</h5>
                      <p className="text-sm text-gray-600">{model.description}</p>
                    </div>
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      formData.pricingModel === model.value
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {formData.pricingModel === model.value && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Unified Pricing Configuration */}
          {formData.pricingModel === 'unified-pricing' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-gray-900 mb-4">Standard Pricing Configuration</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standard Price
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-lg">
                      {formData.currency || 'USD'}
                    </span>
                    <input
                      type="number"
                      value={formData.standardPrice || ''}
                      onChange={(e) => handleInputChange('standardPrice', parseFloat(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="100.00"
                      step="0.01"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pricing Unit
                  </label>
                  <select
                    value={formData.pricingUnit || 'night'}
                    onChange={(e) => handleInputChange('pricingUnit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="night">per night</option>
                    <option value="hour">per hour</option>
                    <option value="day">per day</option>
                    <option value="session">per session</option>
                    <option value="person">per person</option>
                  </select>
                </div>
              </div>

              {/* Price Variations */}
              <div className="mt-4">
                <label className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <span className="font-medium text-gray-900">Weekend Premium</span>
                    <p className="text-sm text-gray-600">Charge more on weekends</p>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.weekendPremium || false}
                      onChange={(e) => handleInputChange('weekendPremium', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </div>
                </label>

                {formData.weekendPremium && (
                  <div className="mt-3 pl-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weekend Multiplier
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={formData.weekendMultiplier || 1.2}
                        onChange={(e) => handleInputChange('weekendMultiplier', parseFloat(e.target.value))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        step="0.1"
                        min="1"
                        max="3"
                      />
                      <span className="text-sm text-gray-600">
                        × (Weekend price: {formData.currency} {((formData.standardPrice || 0) * (formData.weekendMultiplier || 1.2)).toFixed(2)})
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

    {/* Payment Requirements */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment Requirements</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Require Payment</span>
            <p className="text-sm text-gray-600">Customers must pay to complete booking</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requirePayment}
              onChange={(e) => handleInputChange('requirePayment', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {formData.requirePayment && (
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Require Deposit Only</span>
              <p className="text-sm text-gray-600">Collect partial payment upfront</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.depositRequired}
                onChange={(e) => handleInputChange('depositRequired', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        )}
      </div>
    </div>

    {/* Currency Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Currency</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Currency
          </label>
          <select
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="CAD">CAD - Canadian Dollar</option>
            <option value="AUD">AUD - Australian Dollar</option>
            <option value="JPY">JPY - Japanese Yen</option>
            <option value="CHF">CHF - Swiss Franc</option>
            <option value="ZAR">ZAR - South African Rand</option>
          </select>
        </div>

        {formData.depositRequired && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deposit Percentage
            </label>
            <div className="flex">
              <input
                type="number"
                value={formData.depositPercentage}
                onChange={(e) => handleInputChange('depositPercentage', parseInt(e.target.value))}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500"
                min="10"
                max="100"
                step="5"
              />
              <span className="inline-flex items-center px-3 py-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-lg">
                %
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Customers pay {formData.depositPercentage}% upfront, remainder due later
            </p>
          </div>
        )}
      </div>
    </div>

    {/* Payment Methods */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Accepted Payment Methods</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { id: 'card', label: 'Credit/Debit Cards', icon: CreditCard },
          { id: 'paypal', label: 'PayPal', icon: DollarSign },
          { id: 'bank', label: 'Bank Transfer', icon: Building2 },
          { id: 'apple', label: 'Apple Pay', icon: Smartphone },
          { id: 'google', label: 'Google Pay', icon: Smartphone },
          { id: 'crypto', label: 'Cryptocurrency', icon: TrendingUp }
        ].map(method => {
          const MethodIcon = method.icon;
          const isEnabled = formData.paymentMethods.includes(method.id);
          
          return (
            <div
              key={method.id}
              onClick={() => {
                const updated = isEnabled
                  ? formData.paymentMethods.filter(m => m !== method.id)
                  : [...formData.paymentMethods, method.id];
                handleInputChange('paymentMethods', updated);
              }}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isEnabled
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <MethodIcon className={`h-5 w-5 ${isEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className={`font-medium ${isEnabled ? 'text-blue-900' : 'text-gray-700'}`}>
                  {method.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Payment Summary */}
    <div className="bg-blue-50 p-6 rounded-lg">
      <h4 className="font-semibold text-blue-900 mb-2">Payment Configuration Summary</h4>
      <div className="space-y-2 text-sm text-blue-700">
        <div>Payment Required: {formData.requirePayment ? 'Yes' : 'No'}</div>
        <div>Currency: {formData.currency}</div>
        {formData.depositRequired && (
          <div>Deposit: {formData.depositPercentage}% upfront</div>
        )}
        <div>Payment Methods: {formData.paymentMethods.length} enabled</div>
      </div>
    </div>
  </div>
);

// Multi-Vendor Tab Component
const MultiVendorTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Multi-Vendor Settings</h3>
      <p className="text-gray-600">
        Configure marketplace settings for multiple vendors
      </p>
      {formData.businessModel !== 'multi' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Multi-vendor features are only available when business model is set to "Multi-Vendor Marketplace"
              </p>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Vendor Registration */}
    <div className={formData.businessModel !== 'multi' ? 'opacity-50 pointer-events-none' : ''}>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Vendor Registration</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Allow Vendor Registration</span>
            <p className="text-sm text-gray-600">Enable vendors to register and create accounts</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.allowVendorRegistration}
              onChange={(e) => handleInputChange('allowVendorRegistration', e.target.checked)}
              disabled={formData.businessModel !== 'multi'}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Require Vendor Approval</span>
            <p className="text-sm text-gray-600">Manually approve each vendor before they can list</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requireVendorApproval}
              onChange={(e) => handleInputChange('requireVendorApproval', e.target.checked)}
              disabled={formData.businessModel !== 'multi'}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {!formData.requireVendorApproval && (
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Auto-Approve Vendors</span>
              <p className="text-sm text-gray-600">Automatically approve vendor registrations</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.vendorAutoApproval}
                onChange={(e) => handleInputChange('vendorAutoApproval', e.target.checked)}
                disabled={formData.businessModel !== 'multi'}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        )}
      </div>
    </div>

    {/* Commission Settings */}
    <div className={formData.businessModel !== 'multi' ? 'opacity-50 pointer-events-none' : ''}>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Commission & Revenue</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vendor Commission Rate
          </label>
          <div className="flex">
            <input
              type="number"
              value={formData.vendorCommission}
              onChange={(e) => handleInputChange('vendorCommission', parseFloat(e.target.value))}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500"
              min="0"
              max="50"
              step="0.5"
              disabled={formData.businessModel !== 'multi'}
            />
            <span className="inline-flex items-center px-3 py-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-lg">
              %
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Percentage of each booking taken as commission
          </p>
        </div>
      </div>

      <div className="bg-green-50 p-4 rounded-lg">
        <h5 className="font-medium text-green-900 mb-2">Revenue Split Example</h5>
        <div className="text-sm text-green-700 space-y-1">
          <div>Booking Amount: $100</div>
          <div>Your Commission ({formData.vendorCommission}%): ${(100 * formData.vendorCommission / 100).toFixed(2)}</div>
          <div>Vendor Receives: ${(100 * (100 - formData.vendorCommission) / 100).toFixed(2)}</div>
        </div>
      </div>
    </div>

    {/* Vendor Capabilities */}
    <div className={formData.businessModel !== 'multi' ? 'opacity-50 pointer-events-none' : ''}>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Vendor Capabilities</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { key: 'vendorCanManageBookings', label: 'Manage Own Bookings', description: 'Vendors can view and manage their bookings' },
          { key: 'vendorCanSetPricing', label: 'Set Own Pricing', description: 'Vendors control their own pricing' },
          { key: 'vendorCanUploadMedia', label: 'Upload Media', description: 'Vendors can upload photos and videos' },
          { key: 'vendorCanManageAvailability', label: 'Manage Availability', description: 'Vendors control their calendar' },
          { key: 'vendorCanRespondToReviews', label: 'Respond to Reviews', description: 'Vendors can reply to customer reviews' },
          { key: 'vendorCanAccessAnalytics', label: 'View Analytics', description: 'Vendors see their performance data' }
        ].map(capability => (
          <label key={capability.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">{capability.label}</span>
              <p className="text-sm text-gray-600">{capability.description}</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData[capability.key]}
                onChange={(e) => handleInputChange(capability.key, e.target.checked)}
                disabled={formData.businessModel !== 'multi'}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        ))}
      </div>
    </div>
  </div>
);

// SEO & Domain Tab Component  
const SEODomainTab = ({ 
  formData, 
  handleInputChange, 
  handleOgImageUpload, 
  uploadStates, 
  validationErrors, 
  generatePreviewUrl 
}) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">SEO & Domain</h3>
      <p className="text-gray-600">
        Optimize your booking system for search engines and configure custom domains
      </p>
    </div>

    {/* Domain Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Domain Configuration</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Domain
          </label>
          <input
            type="text"
            value={formData.customDomain}
            onChange={(e) => handleInputChange('customDomain', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              validationErrors.customDomain ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="yourdomain.com"
          />
          {validationErrors.customDomain && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.customDomain}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Enter your domain without http:// or www
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Path (Optional)
          </label>
          <input
            type="text"
            value={formData.customPath}
            onChange={(e) => handleInputChange('customPath', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="bookings"
          />
          <p className="text-sm text-gray-500 mt-1">
            Optional path for your booking system
          </p>
        </div>
      </div>

      {generatePreviewUrl() && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h5 className="font-medium text-blue-900 mb-2">Preview URL</h5>
          <p className="text-blue-700 font-mono text-sm break-all">
            {generatePreviewUrl()}
          </p>
        </div>
      )}
    </div>

    {/* SEO Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Search Engine Optimization</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SEO Title
          </label>
          <input
            type="text"
            value={formData.seoTitle}
            onChange={(e) => handleInputChange('seoTitle', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Best Booking System | Your Business Name"
            maxLength="60"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>This appears in search engine results</span>
            <span>{formData.seoTitle.length}/60</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SEO Description
          </label>
          <textarea
            value={formData.seoDescription}
            onChange={(e) => handleInputChange('seoDescription', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Book amazing experiences with our easy-to-use booking system. Secure, fast, and reliable."
            maxLength="160"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>This appears below the title in search results</span>
            <span>{formData.seoDescription.length}/160</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SEO Keywords
          </label>
          <input
            type="text"
            value={formData.seoKeywords}
            onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="booking, reservation, accommodation, appointments"
          />
          <p className="text-sm text-gray-500 mt-1">
            Separate keywords with commas
          </p>
        </div>
      </div>
    </div>

    {/* Social Media Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Social Media & Sharing</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Open Graph Image
          </label>
          <FileUpload
            onFileSelect={handleOgImageUpload}
            currentFile={formData.ogImage}
            onRemove={() => handleInputChange('ogImage', '')}
            accept="image/*"
            maxSize={5}
            label="Upload OG Image"
            uploading={uploadStates.ogImage?.uploading || false}
            error={uploadStates.ogImage?.error || validationErrors.ogImage}
            showPreview={true}
          />
          <p className="text-xs text-gray-500 mt-1">
            Recommended: 1200x630px, used when sharing on social media
          </p>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Enable Structured Data</span>
              <p className="text-sm text-gray-600">Add schema markup for better search visibility</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enableStructuredData}
                onChange={(e) => handleInputChange('enableStructuredData', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Allow Search Engine Indexing</span>
              <p className="text-sm text-gray-600">Let search engines index your booking pages</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allowIndexing}
                onChange={(e) => handleInputChange('allowIndexing', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        </div>
      </div>
    </div>

    {/* SEO Preview */}
    <div className="bg-gray-50 p-6 rounded-lg">
      <h4 className="font-semibold text-gray-900 mb-4">Search Result Preview</h4>
      <div className="bg-white p-4 rounded border">
        <div className="text-blue-600 text-lg hover:underline cursor-pointer line-clamp-1">
          {formData.seoTitle || formData.botName || 'Your Business Name'}
        </div>
        <div className="text-green-600 text-sm">
          {generatePreviewUrl() || 'yourdomain.com'}
        </div>
        <div className="text-gray-700 text-sm mt-1 line-clamp-2">
          {formData.seoDescription || formData.description || 'Your business description will appear here...'}
        </div>
      </div>
    </div>
  </div>
);

// Privacy & Access Tab Component
const PrivacyAccessTab = ({ formData, handleInputChange }) => {
  const [newAuthorizedEmail, setNewAuthorizedEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const addAuthorizedEmail = () => {
    if (!newAuthorizedEmail.trim()) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAuthorizedEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const currentEmails = formData.authorizedEmails || [];
    if (currentEmails.includes(newAuthorizedEmail.trim())) {
      setEmailError('This email is already authorized');
      return;
    }

    handleInputChange('authorizedEmails', [...currentEmails, newAuthorizedEmail.trim()]);
    setNewAuthorizedEmail('');
    setEmailError('');
  };

  const removeAuthorizedEmail = (emailToRemove) => {
    const currentEmails = formData.authorizedEmails || [];
    handleInputChange('authorizedEmails', currentEmails.filter(email => email !== emailToRemove));
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Privacy & Access</h3>
        <p className="text-gray-600">
          Configure who can access your booking system and privacy settings
        </p>
      </div>

      {/* Access Level */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Access Level</h4>
        <div className="space-y-3">
          {[
            { 
              value: 'public', 
              label: 'Public', 
              description: 'Anyone can view and book',
              icon: Globe
            },
            { 
              value: 'private', 
              label: 'Private', 
              description: 'Only authorized users can access',
              icon: Lock
            },
            { 
              value: 'password', 
              label: 'Password Protected', 
              description: 'Requires password to access',
              icon: Shield
            }
          ].map(level => {
            const IconComponent = level.icon;
            return (
              <div
                key={level.value}
                onClick={() => handleInputChange('accessLevel', level.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.accessLevel === level.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-4">
                  <IconComponent className={`h-5 w-5 ${
                    formData.accessLevel === level.value ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{level.label}</h5>
                    <p className="text-sm text-gray-600">{level.description}</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    formData.accessLevel === level.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {formData.accessLevel === level.value && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ENHANCED: Private Access Email Management */}
      {formData.accessLevel === 'private' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Authorized Email Addresses
          </h4>
          <p className="text-sm text-blue-700 mb-6">
            Only users with these email addresses will be able to access your booking system. They will need to sign in with their authorized email.
          </p>

          {/* Add New Email */}
          <div className="mb-6">
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="email"
                  value={newAuthorizedEmail}
                  onChange={(e) => {
                    setNewAuthorizedEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && addAuthorizedEmail()}
                  placeholder="Enter email address to authorize"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    emailError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  }`}
                />
                {emailError && (
                  <p className="text-sm text-red-600 mt-1">{emailError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={addAuthorizedEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
          </div>

          {/* Authorized Emails List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-blue-900">
                Authorized Users ({(formData.authorizedEmails || []).length})
              </span>
              {(formData.authorizedEmails || []).length === 0 && (
                <span className="text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  No users authorized yet
                </span>
              )}
            </div>

            {(formData.authorizedEmails || []).map((email, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-blue-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-gray-900">{email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAuthorizedEmail(email)}
                  className="text-red-600 hover:text-red-700 p-1 rounded"
                  title="Remove authorization"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Access Instructions */}
          {(formData.authorizedEmails || []).length > 0 && (
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <h5 className="font-medium text-blue-900 mb-2">How Private Access Works:</h5>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Authorized users must sign in with their registered email address</li>
                <li>Unregistered visitors will see a sign-in prompt</li>
                <li>You can add or remove authorized emails anytime</li>
                <li>Email verification may be required for first-time users</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Password Protection Settings */}
      {formData.accessLevel === 'password' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Password Protection
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-amber-900 mb-2">
              Access Password
            </label>
            <input
              type="password"
              value={formData.accessPassword || ''}
              onChange={(e) => handleInputChange('accessPassword', e.target.value)}
              placeholder="Enter password for site access"
              className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            <p className="text-sm text-amber-700 mt-2">
              Visitors will need to enter this password to access your booking system
            </p>
          </div>
        </div>
      )}

    {/* Authentication Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Authentication Settings</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Allow Anonymous Viewing</span>
            <p className="text-sm text-gray-600">Visitors can browse without logging in</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.allowAnonymous}
              onChange={(e) => handleInputChange('allowAnonymous', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Require Login for Booking</span>
            <p className="text-sm text-gray-600">Users must create account to make bookings</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.requireLoginForBooking}
              onChange={(e) => handleInputChange('requireLoginForBooking', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Moderate Reviews</span>
            <p className="text-sm text-gray-600">Review customer feedback before publishing</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.moderateReviews}
              onChange={(e) => handleInputChange('moderateReviews', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>
      </div>
    </div>

    {/* Publication Status */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Publication Status</h4>
      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <span className="font-medium text-gray-900">Published</span>
          <p className="text-sm text-gray-600">Make your booking system live and accessible</p>
        </div>
        <div className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={formData.published}
            onChange={(e) => handleInputChange('published', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </div>
      </label>
    </div>

    {/* Privacy Summary */}
    <div className="bg-purple-50 p-6 rounded-lg">
      <h4 className="font-semibold text-purple-900 mb-2">Privacy Configuration Summary</h4>
      <div className="space-y-2 text-sm text-purple-700">
        <div>Access Level: <span className="font-medium capitalize">{formData.accessLevel}</span></div>
        <div>Anonymous Viewing: {formData.allowAnonymous ? 'Allowed' : 'Restricted'}</div>
        <div>Login Required for Booking: {formData.requireLoginForBooking ? 'Yes' : 'No'}</div>
        <div>Review Moderation: {formData.moderateReviews ? 'Enabled' : 'Disabled'}</div>
        <div>Publication Status: {formData.published ? 'Live' : 'Draft'}</div>
      </div>
    </div>
  </div>
);
};

// Notifications Tab Component
const NotificationsTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Notifications</h3>
      <p className="text-gray-600">
        Configure email and SMS notifications for bookings and updates
      </p>
    </div>

    {/* Email Notifications */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h4>
      <div className="space-y-4">
        {[
          { key: 'emailNotifications', label: 'Enable Email Notifications', description: 'Send emails for booking updates' },
          { key: 'bookingConfirmations', label: 'Booking Confirmations', description: 'Email sent when booking is confirmed' },
          { key: 'reminderEmails', label: 'Reminder Emails', description: 'Send reminders before appointments' },
          { key: 'reviewRequests', label: 'Review Requests', description: 'Ask customers to leave reviews after completion' }
        ].map(notification => (
          <label key={notification.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">{notification.label}</span>
              <p className="text-sm text-gray-600">{notification.description}</p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData[notification.key]}
                onChange={(e) => handleInputChange(notification.key, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </div>
          </label>
        ))}
      </div>
    </div>

    {/* SMS Notifications */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">SMS Notifications</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable SMS Notifications</span>
            <p className="text-sm text-gray-600">Send text messages for important updates</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.smsNotifications}
              onChange={(e) => handleInputChange('smsNotifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {formData.smsNotifications && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  SMS notifications require a Twilio account and may incur additional charges.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Notification Schedule */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Reminder Schedule</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Reminder Timing
          </label>
          <select
            value={formData.emailReminderTiming || '24-hours'}
            onChange={(e) => handleInputChange('emailReminderTiming', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="1-hour">1 hour before</option>
            <option value="24-hours">24 hours before</option>
            <option value="48-hours">48 hours before</option>
            <option value="1-week">1 week before</option>
          </select>
        </div>

        {formData.smsNotifications && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMS Reminder Timing
            </label>
            <select
              value={formData.smsReminderTiming || '1-hour'}
              onChange={(e) => handleInputChange('smsReminderTiming', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="30-minutes">30 minutes before</option>
              <option value="1-hour">1 hour before</option>
              <option value="24-hours">24 hours before</option>
            </select>
          </div>
        )}
      </div>
    </div>

    {/* Notification Summary */}
    <div className="bg-blue-50 p-6 rounded-lg">
      <h4 className="font-semibold text-blue-900 mb-2">Notification Summary</h4>
      <div className="space-y-2 text-sm text-blue-700">
        <div>Email Notifications: {formData.emailNotifications ? 'Enabled' : 'Disabled'}</div>
        <div>SMS Notifications: {formData.smsNotifications ? 'Enabled' : 'Disabled'}</div>
        <div>Booking Confirmations: {formData.bookingConfirmations ? 'Enabled' : 'Disabled'}</div>
        <div>Reminder Emails: {formData.reminderEmails ? 'Enabled' : 'Disabled'}</div>
        <div>Review Requests: {formData.reviewRequests ? 'Enabled' : 'Disabled'}</div>
      </div>
    </div>
  </div>
);

// Analytics Tab Component
const AnalyticsTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Analytics & Tracking</h3>
      <p className="text-gray-600">
        Configure analytics and tracking to monitor your booking system performance
      </p>
    </div>

    {/* Basic Analytics */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Basic Analytics</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable Analytics</span>
            <p className="text-sm text-gray-600">Track bookings, views, and performance metrics</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableAnalytics}
              onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Track Conversions</span>
            <p className="text-sm text-gray-600">Monitor booking conversion rates</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.trackConversions}
              onChange={(e) => handleInputChange('trackConversions', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable Heatmaps</span>
            <p className="text-sm text-gray-600">See how users interact with your pages</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableHeatmaps}
              onChange={(e) => handleInputChange('enableHeatmaps', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>
      </div>
    </div>

    {/* Third-party Analytics */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Third-party Analytics</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Analytics ID
          </label>
          <input
            type="text"
            value={formData.googleAnalytics}
            onChange={(e) => handleInputChange('googleAnalytics', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter your Google Analytics tracking ID
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Facebook Pixel ID
          </label>
          <input
            type="text"
            value={formData.facebookPixel}
            onChange={(e) => handleInputChange('facebookPixel', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="1234567890123456"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter your Facebook Pixel ID for conversion tracking
          </p>
        </div>
      </div>
    </div>

    {/* Privacy Notice */}
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex">
        <AlertCircle className="h-5 w-5 text-yellow-400" />
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Privacy Notice</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              Enabling analytics and tracking tools may require cookie consent banners 
              and privacy policy updates to comply with GDPR, CCPA, and other privacy regulations.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Integrations Tab Component
const IntegrationsTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h3>
      <p className="text-gray-600">
        Connect your booking system with external services and tools
      </p>
    </div>

    {/* Maps Integration */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Maps & Location</h4>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Google Maps API Key
        </label>
        <input
          type="text"
          value={formData.googleMapsApiKey}
          onChange={(e) => handleInputChange('googleMapsApiKey', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="AIzaSyBxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        />
        <p className="text-sm text-gray-500 mt-1">
          Required for map display and location features
        </p>
      </div>
    </div>

    {/* Communication */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Communication</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            WhatsApp Business Number
          </label>
          <input
            type="tel"
            value={formData.whatsappNumber}
            onChange={(e) => handleInputChange('whatsappNumber', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="+1234567890"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enable WhatsApp booking support
          </p>
        </div>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Calendar Sync</span>
            <p className="text-sm text-gray-600">Sync bookings with external calendars</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.calendarSync}
              onChange={(e) => handleInputChange('calendarSync', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>
      </div>
    </div>

    {/* Automation */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Automation & Webhooks</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zapier Webhook URL
          </label>
          <input
            type="url"
            value={formData.zapierWebhook}
            onChange={(e) => handleInputChange('zapierWebhook', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="https://hooks.zapier.com/hooks/catch/..."
          />
          <p className="text-sm text-gray-500 mt-1">
            Trigger Zapier workflows on booking events
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slack Webhook URL
          </label>
          <input
            type="url"
            value={formData.slackWebhook}
            onChange={(e) => handleInputChange('slackWebhook', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="https://hooks.slack.com/services/..."
          />
          <p className="text-sm text-gray-500 mt-1">
            Send booking notifications to Slack
          </p>
        </div>
      </div>
    </div>

    {/* Integration Status */}
    <div className="bg-green-50 p-6 rounded-lg">
      <h4 className="font-semibold text-green-900 mb-4">Integration Status</h4>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-green-700">Google Maps:</span>
          <span className={`font-medium ${formData.googleMapsApiKey ? 'text-green-800' : 'text-red-600'}`}>
            {formData.googleMapsApiKey ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-green-700">WhatsApp:</span>
          <span className={`font-medium ${formData.whatsappNumber ? 'text-green-800' : 'text-red-600'}`}>
            {formData.whatsappNumber ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-green-700">Calendar Sync:</span>
          <span className={`font-medium ${formData.calendarSync ? 'text-green-800' : 'text-red-600'}`}>
            {formData.calendarSync ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-green-700">Zapier:</span>
          <span className={`font-medium ${formData.zapierWebhook ? 'text-green-800' : 'text-red-600'}`}>
            {formData.zapierWebhook ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-green-700">Slack:</span>
          <span className={`font-medium ${formData.slackWebhook ? 'text-green-800' : 'text-red-600'}`}>
            {formData.slackWebhook ? 'Connected' : 'Not Connected'}
          </span>
        </div>
      </div>
    </div>
  </div>
);

// Advanced Tab Component
const AdvancedTab = ({ formData, handleInputChange }) => (
  <div className="space-y-8">
    <div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Advanced Settings</h3>
      <p className="text-gray-600">
        Advanced configuration options for developers and power users
      </p>
    </div>

    {/* Custom Code */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Custom Code</h4>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom CSS
          </label>
          <textarea
            value={formData.customCSS}
            onChange={(e) => handleInputChange('customCSS', e.target.value)}
            rows={8}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="/* Add your custom CSS here */
.booking-card {
  background: #ffffff;
  border-radius: 8px;
}"
          />
          <p className="text-sm text-gray-500 mt-1">
            Add custom CSS to override default styles
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom JavaScript
          </label>
          <textarea
            value={formData.customJS}
            onChange={(e) => handleInputChange('customJS', e.target.value)}
            rows={8}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="// Add your custom JavaScript here
// Example: Google Analytics event tracking
gtag('event', 'booking_started', {
  'event_category': 'engagement'
});"
          />
          <p className="text-sm text-gray-500 mt-1">
            Add custom JavaScript for tracking or functionality
          </p>
        </div>
      </div>
    </div>

    {/* API Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">API Settings</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable API Access</span>
            <p className="text-sm text-gray-600">Allow external applications to access your booking data</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableAPI}
              onChange={(e) => handleInputChange('enableAPI', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {formData.enableAPI && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => handleInputChange('apiKey', e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder="Auto-generated API key"
                  readOnly
                />
                <button
                  onClick={() => handleInputChange('apiKey', `pk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Generate
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL
              </label>
              <input
                type="url"
                value={formData.webhookUrl}
                onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://your-api.com/webhooks/booking"
              />
              <p className="text-sm text-gray-500 mt-1">
                Receive booking events at this URL
              </p>
            </div>
          </>
        )}
      </div>
    </div>

    {/* System Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable Backups</span>
            <p className="text-sm text-gray-600">Automatically backup your booking data</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableBackups}
              onChange={(e) => handleInputChange('enableBackups', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Maintenance Mode</span>
            <p className="text-sm text-gray-600">Temporarily disable booking system for maintenance</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.maintenanceMode}
              onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {formData.maintenanceMode && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Maintenance Mode Active</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    Your booking system is currently in maintenance mode. Visitors will see a maintenance message 
                    and won't be able to make new bookings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Database & Performance */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Database & Performance</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cache Duration (minutes)
          </label>
          <select
            value={formData.cacheDuration || 30}
            onChange={(e) => handleInputChange('cacheDuration', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={5}>5 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={120}>2 hours</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Concurrent Bookings
          </label>
          <input
            type="number"
            value={formData.maxConcurrentBookings || 100}
            onChange={(e) => handleInputChange('maxConcurrentBookings', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            min="1"
            max="1000"
          />
        </div>
      </div>
    </div>

    {/* Security Settings */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Security</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable Rate Limiting</span>
            <p className="text-sm text-gray-600">Prevent abuse by limiting requests per IP</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableRateLimit || false}
              onChange={(e) => handleInputChange('enableRateLimit', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Enable CAPTCHA</span>
            <p className="text-sm text-gray-600">Add CAPTCHA verification to booking forms</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableCaptcha || false}
              onChange={(e) => handleInputChange('enableCaptcha', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>
      </div>
    </div>

    {/* Developer Tools */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Developer Tools</h4>
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Debug Mode</span>
            <p className="text-sm text-gray-600">Enable detailed error logging and debug information</p>
          </div>
          <div className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.debugMode || false}
              onChange={(e) => handleInputChange('debugMode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </div>
        </label>

        {formData.debugMode && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Debug Mode Warning</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Debug mode should only be enabled during development or troubleshooting. 
                    It may expose sensitive information and affect performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Configuration Export/Import */}
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Configuration Management</h4>
      <div className="flex gap-4">
        <button
          onClick={() => {
            const config = JSON.stringify(formData, null, 2);
            const blob = new Blob([config], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'booking-settings.json';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export Settings
        </button>

        <label className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 cursor-pointer">
          <Upload className="h-4 w-4" />
          Import Settings
          <input
            type="file"
            accept=".json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                  try {
                    const importedSettings = JSON.parse(event.target.result);
                    // Merge imported settings with current form data
                    Object.keys(importedSettings).forEach(key => {
                      handleInputChange(key, importedSettings[key]);
                    });
                    alert('Settings imported successfully!');
                  } catch (error) {
                    alert('Failed to import settings. Please check the file format.');
                  }
                };
                reader.readAsText(file);
              }
            }}
          />
        </label>
      </div>
    </div>

    {/* Advanced Summary */}
    <div className="bg-gray-900 text-white p-6 rounded-lg">
      <h4 className="font-semibold mb-4">Advanced Configuration Summary</h4>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-300">API Access:</span>
          <span className="ml-2 font-medium">{formData.enableAPI ? 'Enabled' : 'Disabled'}</span>
        </div>
        <div>
          <span className="text-gray-300">Backups:</span>
          <span className="ml-2 font-medium">{formData.enableBackups ? 'Enabled' : 'Disabled'}</span>
        </div>
        <div>
          <span className="text-gray-300">Maintenance:</span>
          <span className="ml-2 font-medium">{formData.maintenanceMode ? 'Active' : 'Inactive'}</span>
        </div>
        <div>
          <span className="text-gray-300">Debug Mode:</span>
          <span className="ml-2 font-medium">{formData.debugMode ? 'Enabled' : 'Disabled'}</span>
        </div>
        <div>
          <span className="text-gray-300">Custom CSS:</span>
          <span className="ml-2 font-medium">{formData.customCSS ? 'Added' : 'None'}</span>
        </div>
        <div>
          <span className="text-gray-300">Custom JS:</span>
          <span className="ml-2 font-medium">{formData.customJS ? 'Added' : 'None'}</span>
        </div>
      </div>
    </div>
  </div>
);